import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Star, Send } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { PageHeader, Field, Empty } from "../components/Common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

export const Stars = ({ n, size = "h-4 w-4" }) => (
  <span className="inline-flex">{[1, 2, 3, 4, 5].map((i) => <Star key={i} className={`${size} ${i <= n ? "fill-[hsl(var(--brand-accent))] text-[hsl(var(--brand-accent))]" : "text-muted-foreground/40"}`} />)}</span>
);

function Reviews() {
  const [list, setList] = useState([]);
  const load = useCallback(() => api.get("/reviews").then((r) => setList(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const patch = async (r, status, reply = r.reply) => { try { await api.patch(`/reviews/${r.id}`, { status, reply }); load(); } catch (e) { toast.error(errMsg(e)); } };
  if (!list.length) return <Empty text="No reviews yet" testId="reviews-empty" />;
  return (
    <div className="space-y-3" data-testid="reviews-list">
      {list.map((r) => (
        <div key={r.id} className="rounded-[var(--radius)] border bg-card p-5 space-y-2" data-testid={`review-${r.id}`}>
          <div className="flex flex-wrap items-center gap-3"><Stars n={r.rating} /><b>{r.name}</b><span className="text-xs text-muted-foreground">{r.email} · {r.created_at.slice(0, 10)}</span>
            <span className={`ml-auto text-xs rounded-full px-2.5 py-1 ${r.status === "approved" ? "bg-emerald-500/15 text-emerald-800" : r.status === "hidden" ? "bg-zinc-500/15" : "bg-amber-500/15 text-amber-800"}`}>{r.status}</span></div>
          <p className="text-sm whitespace-pre-wrap">{r.text}</p>
          {r.reply && <p className="text-sm text-muted-foreground border-l-2 pl-3">Reply: {r.reply}</p>}
          <div className="flex gap-2">
            {r.status !== "approved" && <Button size="sm" className="rounded-full" data-testid={`review-approve-${r.id}`} onClick={() => patch(r, "approved")}>Publish</Button>}
            {r.status !== "hidden" && <Button size="sm" variant="ghost" data-testid={`review-hide-${r.id}`} onClick={() => patch(r, "hidden")}>Hide</Button>}
            <Button size="sm" variant="ghost" data-testid={`review-reply-${r.id}`} onClick={() => { const t = window.prompt("Public reply", r.reply); if (t !== null) patch(r, r.status, t); }}>Reply</Button>
          </div>
        </div>
      ))}
    </div>
  );
}

function PostEditor({ id, open, onOpenChange, onSaved }) {
  const [f, setF] = useState({ title: "", excerpt: "", body: "", cover: "", status: "draft" });
  useEffect(() => {
    if (!open) return;
    if (id) api.get(`/posts/${id}`).then((r) => setF(r.data)); else setF({ title: "", excerpt: "", body: "", cover: "", status: "draft" });
  }, [open, id]);
  const cover = (file) => {
    const img = new Image();
    img.onload = () => { const s = Math.min(1, 1200 / img.width); const c = document.createElement("canvas"); c.width = img.width * s; c.height = img.height * s;
      c.getContext("2d").drawImage(img, 0, 0, c.width, c.height); setF((p) => ({ ...p, cover: c.toDataURL("image/jpeg", 0.75) })); };
    img.src = URL.createObjectURL(file);
  };
  const save = async (status) => {
    const body = { title: f.title, excerpt: f.excerpt, body: f.body, cover: f.cover, status };
    try { id ? await api.put(`/posts/${id}`, body) : await api.post("/posts", body); toast.success(status === "published" ? "Published" : "Draft saved"); onOpenChange(false); onSaved(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="post-editor">
        <DialogHeader><DialogTitle>Article</DialogTitle></DialogHeader>
        <Field label="Title"><Input data-testid="post-title-input" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} /></Field>
        <Field label="Short summary (shown in list and email)"><Textarea rows={2} data-testid="post-excerpt-input" value={f.excerpt} onChange={(e) => setF({ ...f, excerpt: e.target.value })} /></Field>
        <Field label="Cover image">
          <div className="flex items-center gap-3">{f.cover && <img src={f.cover} alt="" className="h-14 w-24 object-cover rounded" />}
            <input type="file" accept="image/*" data-testid="post-cover-input" className="text-xs" onChange={(e) => e.target.files[0] && cover(e.target.files[0])} /></div>
        </Field>
        <Field label="Article (leave an empty line between paragraphs; start a line with ## for a heading)">
          <Textarea rows={14} data-testid="post-body-input" value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} />
        </Field>
        <div className="flex gap-2">
          <Button variant="outline" className="rounded-full" data-testid="post-save-draft-btn" disabled={!f.title} onClick={() => save("draft")}>Save draft</Button>
          <Button className="rounded-full" data-testid="post-publish-btn" disabled={!f.title} onClick={() => save("published")}>Publish</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Blog() {
  const [list, setList] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => api.get("/posts").then((r) => setList(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const send = async (p) => {
    if (!window.confirm(`Email "${p.title}" to all subscribers?`)) return;
    try { const { data } = await api.post(`/posts/${p.id}/send`); toast.success(`Sent to ${data.sent} subscribers`); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-4">
      <Button className="rounded-full" data-testid="new-post-btn" onClick={() => setEdit("new")}>New article</Button>
      {!list.length && <Empty text="No articles yet" />}
      {list.map((p) => (
        <div key={p.id} className="rounded-[var(--radius)] border bg-card p-4 flex flex-wrap items-center gap-4" data-testid={`post-row-${p.slug}`}>
          {p.cover && <img src={p.cover} alt="" className="h-12 w-20 object-cover rounded" />}
          <div className="flex-1 min-w-[180px]"><div className="font-semibold">{p.title}</div>
            <div className="text-xs text-muted-foreground">{p.status}{p.sent_at && ` · emailed to ${p.sent_count}`}</div></div>
          <Button size="sm" variant="ghost" data-testid={`post-edit-${p.slug}`} onClick={() => setEdit(p.id)}>Edit</Button>
          {p.status === "published" && <Button size="sm" variant="outline" className="rounded-full" data-testid={`post-send-${p.slug}`} onClick={() => send(p)}><Send className="h-3.5 w-3.5 mr-1" />Email subscribers</Button>}
        </div>
      ))}
      <PostEditor id={edit === "new" ? null : edit} open={!!edit} onOpenChange={(o) => !o && setEdit(null)} onSaved={load} />
    </div>
  );
}

function Subscribers() {
  const [list, setList] = useState([]);
  useEffect(() => { api.get("/subscribers").then((r) => setList(r.data)); }, []);
  if (!list.length) return <Empty text="No subscribers yet" testId="subscribers-empty" />;
  return (
    <div className="rounded-[var(--radius)] border bg-card divide-y" data-testid="subscribers-list">
      {list.map((c) => <a key={c.id} href={`/app/clients/${c.id}`} className="flex gap-4 px-5 py-3 text-sm hover:bg-muted/40"><b>{c.first_name} {c.last_name}</b><span className="text-muted-foreground">{c.email}</span><span className="ml-auto text-xs">{c.source}</span></a>)}
    </div>
  );
}

function ContentSettings() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/content/settings").then((r) => setS(r.data)); }, []);
  if (!s) return null;
  const save = async () => { try { setS((await api.put("/content/settings", s)).data); toast.success("Saved"); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="max-w-2xl space-y-4">
      <Field label="Your Google review link (Google Business Profile → Ask for reviews → copy link)"><Input data-testid="google-review-url-input" placeholder="https://g.page/r/..." value={s.google_review_url} onChange={(e) => setS({ ...s, google_review_url: e.target.value })} /></Field>
      <Field label="Blog title"><Input data-testid="blog-title-input" value={s.blog_title} onChange={(e) => setS({ ...s, blog_title: e.target.value })} /></Field>
      <Field label="Blog intro"><Textarea data-testid="blog-intro-input" value={s.blog_intro} onChange={(e) => setS({ ...s, blog_intro: e.target.value })} /></Field>
      <Button className="rounded-full" data-testid="content-settings-save-btn" onClick={save}>Save</Button>
    </div>
  );
}

export default function Content() {
  return (
    <div>
      <PageHeader title="Reviews & Blog" />
      <Tabs defaultValue="reviews">
        <TabsList className="mb-6 flex-wrap h-auto">{["reviews", "blog", "subscribers", "settings"].map((k) => <TabsTrigger key={k} value={k} className="capitalize" data-testid={`content-tab-${k}`}>{k}</TabsTrigger>)}</TabsList>
        <TabsContent value="reviews"><Reviews /></TabsContent>
        <TabsContent value="blog"><Blog /></TabsContent>
        <TabsContent value="subscribers"><Subscribers /></TabsContent>
        <TabsContent value="settings"><ContentSettings /></TabsContent>
      </Tabs>
    </div>
  );
}
